import { createContext, ReactNode, use, useEffect, useState } from 'react';
import { createTimeString, TimeString } from '../../../common/src/time_string.ts';

// Minute-resolution "current wall-clock time." Seconds are always "00" — the provider
// schedules updates at each minute boundary, so callers downstream of the context get
// a stable value within a minute rather than a ticking clock.
export const CurrentTimeContext = createContext<TimeString | undefined>(undefined);

export function useCurrentTime(): TimeString {
  return use(CurrentTimeContext) ?? nowTimeToMinute();
}

export const CurrentTimeProvider = ({ children }: { children: ReactNode }) => {
  const [liveTime, setLiveTime] = useState<TimeString>(() => nowTimeToMinute());

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;

    const scheduleNextUpdate = () => {
      const now = new Date();
      const msUntilNextMinute = (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
      timeoutId = setTimeout(() => {
        setLiveTime(nowTimeToMinute());
        scheduleNextUpdate();
      }, msUntilNextMinute);
    };

    // setTimeout is unreliable across tab suspension (especially on mobile); re-check on wake.
    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      setLiveTime(nowTimeToMinute());
      clearTimeout(timeoutId);
      scheduleNextUpdate();
    };

    scheduleNextUpdate();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);

  return <CurrentTimeContext value={liveTime}>{children}</CurrentTimeContext>;
};

function nowTimeToMinute(): TimeString {
  const now = new Date();
  return createTimeString(now.getHours(), now.getMinutes());
}
